import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { usePresetColors, useRecordingAnalytics } from '../../../hooks';
import { selectCaptionSettings, selectSelectedClip } from '../../../selector';
import {
  PrimaryColors,
  SecondaryColors,
  TertiaryColors,
} from '../../../theme/constants';
import { ColorPicker } from '../../color-presets/color-picker';
import { expandHexColor } from '../../color-presets/utils';

type ActiveWordColorControlsProps = {
  disabled?: boolean;
};

export const ActiveWordColorControls = ({
  disabled = false,
}: ActiveWordColorControlsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { trackRecordingEvent } = useRecordingAnalytics();
  const { highlight_color } = useAppSelector(selectCaptionSettings);
  const selectedClip = useAppSelector(selectSelectedClip);
  const { currentPreset } = usePresetColors();

  // Extract theme colors from preset config for the color picker
  const themeColors = useMemo(() => {
    const config = currentPreset?.config || {};
    const allColorKeys = [
      ...PrimaryColors,
      ...SecondaryColors,
      ...TertiaryColors,
    ];
    return allColorKeys
      .map(key => config[key] as string)
      .filter(
        (color): color is string =>
          typeof color === 'string' && color.startsWith('#'),
      );
  }, [currentPreset?.config]);

  const [inputValue, setInputValue] = useState(highlight_color.toUpperCase());

  // Sync input value when color changes (e.g., preset change)
  useEffect(() => {
    setInputValue(highlight_color.toUpperCase());
  }, [highlight_color]);

  const handleColorChange = useCallback(
    (newColor: string) => {
      // Only dispatch if a clip is selected
      if (!selectedClip) return;
      dispatch({
        type: '[recording] captions active word color changed',
        payload: {
          color: newColor,
        },
      });
      setInputValue(newColor.toUpperCase());
      const isThemeColor = themeColors.some(
        c => c.toUpperCase() === newColor.toUpperCase(),
      );
      trackRecordingEvent('ch_creator_lab_appearance_active_word_color', {
        themeColor: isThemeColor ? 'yes' : 'no',
        colorCode: newColor,
      });
    },
    [dispatch, selectedClip, themeColors, trackRecordingEvent],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Only handle if a clip is selected
      if (!selectedClip) return;
      const value = e.target.value.toUpperCase();
      setInputValue(value);

      // Only dispatch for valid 6-digit hex while typing
      // 3-digit hex is handled on blur to allow user to continue typing
      if (/^#[0-9A-F]{6}$/i.test(value)) {
        dispatch({
          type: '[recording] captions active word color changed',
          payload: {
            color: value,
          },
        });
      }
    },
    [dispatch, selectedClip],
  );

  const handleInputBlur = useCallback(() => {
    // Only handle if a clip is selected
    if (!selectedClip) return;
    // Validate hex color format (3 or 6 digits)
    if (/^#[0-9A-F]{3}$/i.test(inputValue)) {
      // Expand 3-digit to 6-digit, update input and dispatch
      const expandedColor = expandHexColor(inputValue);
      setInputValue(expandedColor);
      dispatch({
        type: '[recording] captions active word color changed',
        payload: {
          color: expandedColor,
        },
      });
    } else if (!/^#[0-9A-F]{6}$/i.test(inputValue)) {
      // Reset to valid color if input is invalid
      setInputValue(highlight_color.toUpperCase());
    }
  }, [inputValue, highlight_color, dispatch, selectedClip]);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[14px] font-medium text-white leading-[20px] tracking-[0px] m-0">
        {t('content-recording.appearance.captions.active-word-color.label')}
      </p>
      <div className="flex items-center gap-2">
        <div className="relative h-[32px] w-[32px] rounded-[8px] overflow-visible">
          <ColorPicker
            value={highlight_color}
            onChange={handleColorChange}
            title={t(
              'content-recording.appearance.captions.active-word-color.label',
            )}
            shape="rectangle"
            placement="bottom-start"
            themeColors={themeColors}
            disabled={disabled}
          />
        </div>
        <div className="flex-1">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={disabled}
            aria-label={t(
              'content-recording.appearance.captions.active-word-color.label',
            )}
            className="w-[101px] h-[26px] bg-spring-neutral-b5/90 rounded-[4px] text-[14px] font-semibold text-white text-center leading-[21px] border-none outline-none"
            maxLength={7}
          />
        </div>
      </div>
    </div>
  );
};
